import {
  SimplePropsAdder,
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addRef,
  addWrapper,
  branch,
  flowMax,
  returns,
} from 'ad-hok'
import { addEffectOnMount } from 'ad-hok-utils'
import cx from 'classnames'
import React, { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { Navigate } from 'react-router'
import { addApi } from 'shared/src/api'
import { UserListPublic } from 'shared/src/api/types'
import { addOptionalIntPathParam } from 'shared/src/utils/addNavigate'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { trackEditFeedView } from 'shared/src/utils/analytics'
import { addAuthContext } from 'shared/src/utils/auth'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { typedAs } from 'shared/src/utils/typedAs'

import { RodeoAndEditTabs } from 'components/RodeoAndEditTabs'
import { Footer } from 'components/footer'
import { addMenuPage } from 'components/menu'
import { Tabs } from 'components/menu/Tabs'
import { addRegisterPage } from 'components/register'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { ConstrainWidth } from 'utils/layout'
import {
  RodeoOrEditViewType,
  addRodeoOrEditViewContextProvider,
} from 'utils/rodeoOrEditViewContext'
import { setPageTitle } from 'utils/title'
import { addLoadingAndErrorIndicators } from '../../utils/addLoadingAndErrorIndicators'
import { NavBar } from '../navbar'
import { Edit } from './Edit'
import { Register } from './Register'
import { Banner } from 'components/banners/Banner'
import { GO2APP_URL } from 'utils/urls'

const NOT_LOGGED_IN_EDITS = 2

const flipLassoForIndex = (index: number) => index % 2 !== 0

const useStyles = createUseStyles({
  container: {
    paddingTop: 170,
  },
  blurredBackground: {
    '-webkit-filter': 'blur(6px)',
    '-moz-filter': 'blur(6px)',
    '-o-filter': 'blur(6px)',
    '-ms-filter': 'blur(6px)',
    filter: 'blur(6px)',
    overflow: 'hidden',
    pointerEvents: 'none',
    position: 'relative',
    height: '100dvh',
    widht: '100%',
  },
})

const stateIdFromUserEditPublicId = (userListPublic: UserListPublic) =>
  `${userListPublic.id}-${userListPublic.user.id}`

interface AddExpandedHelpers {
  isExpanded: (userListPublic: UserListPublic) => boolean
  onToggleExpand: (userListPublic: UserListPublic) => () => void
}

const addExpandedHelpers: SimplePropsAdder<AddExpandedHelpers> = flowMax(
  addNavigationState(
    'expandedEditsNavigationState',
    'setExpandedEditsNavigationState',
    ''
  ),
  addProps(({ expandedEditsNavigationState }) => ({
    expandedEditsStateIds: expandedEditsNavigationState.split(','),
  })),
  addHandlers({
    isExpanded:
      ({ expandedEditsStateIds }) =>
      (userListPublic: UserListPublic) =>
        expandedEditsStateIds.includes(
          stateIdFromUserEditPublicId(userListPublic)
        ),
    onToggleExpand:
      ({ expandedEditsStateIds, setExpandedEditsNavigationState }) =>
      (userListPublic: UserListPublic) =>
      () => {
        const stateId = stateIdFromUserEditPublicId(userListPublic)

        if (expandedEditsStateIds.includes(stateId)) {
          setExpandedEditsNavigationState(
            expandedEditsStateIds.filter((id) => id !== stateId).join(',')
          )
        } else {
          setExpandedEditsNavigationState(
            expandedEditsStateIds.concat(stateId).join(',')
          )
        }
      },
  })
)

export const Edits: FC = flowMax(
  addDisplayName('Edits'),
  addAuthContext,
  addProps({
    rodeoOrEditView: typedAs<RodeoOrEditViewType>('edit'),
  }),
  addRodeoOrEditViewContextProvider,
  addRegisterPage,
  addMenuPage,
  addOptionalIntPathParam('editId'),
  addTranslationHelpers,
  setPageTitle(({ t }) => t('edits.title')),
  addApi,
  addDataLoadingFromPromise(
    'featuredEdits',
    ({ api }) => api.edits.featuredEdits(),
    [],
    () => 'featuredEdits'
  ),
  addLoadingAndErrorIndicators('featuredEdits', { redirectTo404IfError: true }),
  branch(
    ({ featuredEdits }) => featuredEdits.length === 0,
    returns(() => <Navigate to="/" />)
  ),
  addProps(({ editId, featuredEdits }) => ({
    editId: editId ?? featuredEdits[0].id,
  })),
  addWrapper((render, { editId }) => (
    <React.Fragment key={`${editId}`}>{render()}</React.Fragment>
  )),
  addDataLoadingFromPromise(
    'edits',
    ({ editId, api }) => api.userLists.editFeed(editId),
    ['editId'],
    ({ editId }) => `${editId}-edits`
  ),
  addLoadingAndErrorIndicators('edits', { redirectTo404IfError: true }),
  addExpandedHelpers,
  addRef('editFeedDivRef', typedAs<HTMLDivElement | null>(null)),
  addHandlers({
    scrollToTop:
      ({ editFeedDivRef }) =>
      () => {
        editFeedDivRef.current?.scrollIntoView()
      },
  }),
  addEffect(
    ({ scrollToTop, editId }) =>
      () => {
        if (editId) {
          scrollToTop()
        }
      },
    ['editId']
  ),
  addProps(({ editId, featuredEdits }) => ({
    currentIndex: featuredEdits.findIndex((edit) => edit.id === editId),
  })),
  addProps(({ currentIndex, isAuthenticated }) => ({
    showRegister:
      false && !isAuthenticated && currentIndex >= NOT_LOGGED_IN_EDITS,
  })),
  addEffectOnMount(({ editId, showRegister, api }) => () => {
    if (!showRegister) {
      trackEditFeedView(editId, api)
    }
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({
    showRegister,
    featuredEdits,
    editId,
    edits,
    isExpanded,
    onToggleExpand,
    editFeedDivRef,
    classes,
    t,
  }) => (
    <ConstrainWidth>
      <NavBar
        CenterComponent={RodeoAndEditTabs}
        showProfileButton
        showGradient
        showRoundupButton
      />
      <Tabs currentFeedId={editId.toString()} data={{ edits: featuredEdits }} />
      {showRegister && <Register />}
      <div className={cx(classes.blurredBackground)}>
        <div ref={editFeedDivRef} className={classes.container}>
          {edits.map((edit, index) => (
            <Edit
              mode={'combined'}
              list={edit}
              flipLasso={flipLassoForIndex(index)}
              isExpanded={isExpanded(edit)}
              onToggleExpand={onToggleExpand(edit)}
              key={edit.user.id.toString() + '-' + edit.id.toString()}
            />
          ))}
        </div>
        <Footer />
      </div>

      <Banner
        banner={{
          unDismissable: true,
          noHider: true,
          accessibleMenu: true,
          textTranslationKey: 'banners.mobileOpenDownloadApp.appName',
          onCloseAnalyticsEvent: {
            options: {
              category: 'general',
              action: 'banner_open_download_in_app_close',
            },
          },
          link: {
            title: t('banners.mobileOpenDownloadApp.exclusiveTitle'),
            navigateTo: window.location.href.replace(
              `${window.location.protocol}//${window.location.hostname}${
                window.location.port ? ':' + window.location.port : ''
              }`,
              GO2APP_URL.replace(/\/$/, '')
            ),
            external: false,
            onClickAnalyticsEvent: {
              options: {
                category: 'general',
                action: 'banner_open_download_in_app_click',
              },
            },
          },
        }}
        onAction={() => {}}
      />
    </ConstrainWidth>
  )
)
